/* eslint-disable max-lines -- Storyblok has many components */

import dynamic from 'next/dynamic'
import { type ComponentType } from 'react'

import { type StoryblokComponentProps } from '../model'
import {
  type StoryblokComponentName,
  type StoryblokComponentType,
} from '../model/storyblokComponents'

import { StoryblokAdBanner } from './StoryblokAdBanner/StoryblokAdBanner'
import { StoryblokAkeneoCategoryPage } from './StoryblokAkeneoCategoryPage'
import { StoryblokBrandPage } from './StoryblokBrandPage'
import { StoryblokButtonV2 } from './StoryblokButtonV2'
import {
  StoryblokCategoryAdBannersSmall,
  StoryblokCategoryAdBannerWide,
} from './StoryblokCategoryAdBanner'
import { StoryblokCrossSellSlider } from './StoryblokCrossSellSlider'
import { withStoryblokSuspenseErrorBoundary } from './StoryblokErrorBoundary'
import { StoryblokFlex } from './StoryblokFlex/StoryblokFlex'
import { StoryblokGrid, StoryblokGridCell } from './StoryblokGrid'
import { StoryblokHeadingV2 } from './StoryblokHeadingV2'
import { StoryblokPage } from './StoryblokPage'
import { StoryblokSection } from './StoryblokSection'
import { StoryblokSectionV2 } from './StoryblokSectionV2'

const StoryblokRichText = withStoryblokSuspenseErrorBoundary(
  dynamic(async () =>
    import('./StoryblokRichText').then((module) => module.StoryblokRichText),
  ),
)

const StoryblokAdvicePage = withStoryblokSuspenseErrorBoundary(
  dynamic(async () =>
    import('./StoryblokAdvicePage').then(
      (module) => module.StoryblokAdvicePage,
    ),
  ),
)

const StoryblokBlogArticle = withStoryblokSuspenseErrorBoundary(
  dynamic(async () =>
    import('./StoryblokBlogArticle').then(
      (module) => module.StoryblokBlogArticle,
    ),
  ),
)

const StoryblokNotification = withStoryblokSuspenseErrorBoundary(
  dynamic(async () =>
    import('./StoryblokNotification').then(
      (module) => module.StoryblokNotification,
    ),
  ),
)

const StoryblokImageTextHorizontalCard = withStoryblokSuspenseErrorBoundary(
  dynamic(async () =>
    import('./StoryblokImageTextHorizontalCard').then(
      (module) => module.StoryblokImageTextHorizontalCard,
    ),
  ),
)

const StoryblokButton = withStoryblokSuspenseErrorBoundary(
  dynamic(async () =>
    import('./StoryblokButton').then((module) => module.StoryblokButton),
  ),
)

const StoryblokHeaderBanner = withStoryblokSuspenseErrorBoundary(
  dynamic(async () =>
    import('./StoryblokHeaderBanner').then(
      (module) => module.StoryblokHeaderBanner,
    ),
  ),
)

const StoryblokCardCarousel = dynamic(async () =>
  import('./StoryblokCardCarousel').then(
    (module) => module.StoryblokCardCarousel,
  ),
)

const StoryblokImageCard = withStoryblokSuspenseErrorBoundary(
  dynamic(async () =>
    import('./StoryblokImageCard').then((module) => module.StoryblokImageCard),
  ),
)

const StoryblokCategorySection = withStoryblokSuspenseErrorBoundary(
  dynamic(async () =>
    import('./StoryblokCategorySection').then(
      (module) => module.StoryblokCategorySection,
    ),
  ),
)

const StoryblokVoucherCard = withStoryblokSuspenseErrorBoundary(
  dynamic(async () =>
    import('./StoryblokVoucherCard').then(
      (module) => module.StoryblokVoucherCard,
    ),
  ),
)

const StoryblokVoucherCardV2 = withStoryblokSuspenseErrorBoundary(
  dynamic(async () =>
    import('./StoryblokVoucherCardV2').then(
      (module) => module.StoryblokVoucherCardV2,
    ),
  ),
)

const StoryblokColumnCard = withStoryblokSuspenseErrorBoundary(
  dynamic(async () =>
    import('./StoryblokColumnCard').then(
      (module) => module.StoryblokColumnCard,
    ),
  ),
)

const StoryblokStepStar = withStoryblokSuspenseErrorBoundary(
  dynamic(async () =>
    import('./StoryblokStepStar').then((module) => module.StoryblokStepStar),
  ),
)

const StoryblokImageV2 = withStoryblokSuspenseErrorBoundary(
  dynamic(async () =>
    import('./StoryblokImageV2').then((module) => module.StoryblokImageV2),
  ),
)

const StoryblokSpacing = withStoryblokSuspenseErrorBoundary(
  dynamic(async () =>
    import('./StoryblokSpacing').then((module) => module.StoryblokSpacing),
  ),
)

const StoryblokFlexRow = withStoryblokSuspenseErrorBoundary(
  dynamic(async () =>
    import('./StoryblokFlexRow').then((module) => module.StoryblokFlexRow),
  ),
)

const StoryblokProductCard = withStoryblokSuspenseErrorBoundary(
  dynamic(async () =>
    import('./StoryblokProductCard').then(
      (module) => module.StoryblokProductCard,
    ),
  ),
)

const StoryblokPlainTextFaq = withStoryblokSuspenseErrorBoundary(
  dynamic(async () =>
    import('./StoryblokPlainTextFaq').then(
      (module) => module.StoryblokPlainTextFaq,
    ),
  ),
)

const StoryblokCategoryWideTeaserAd = withStoryblokSuspenseErrorBoundary(
  dynamic(async () =>
    import('./StoryblokCategoryWideTeaserAd').then(
      (module) => module.StoryblokCategoryWideTeaserAd,
    ),
  ),
)

const StoryblokServicesList = withStoryblokSuspenseErrorBoundary(
  dynamic(async () =>
    import('./StoryblokServicesList/StoryblokServicesList').then(
      (module) => module.StoryblokServicesList,
    ),
  ),
)

const StoryblokCard = withStoryblokSuspenseErrorBoundary(
  dynamic(async () =>
    import('./StoryblokCard/StoryblokCard').then(
      (module) => module.StoryblokCard,
    ),
  ),
)

const StoryblokInfoCard = withStoryblokSuspenseErrorBoundary(
  dynamic(async () =>
    import('./StoryblokInfoCard/StoryblokInfoCard').then(
      (module) => module.StoryblokInfoCard,
    ),
  ),
)

type StoryblokComponentMap = {
  [key in StoryblokComponentName]: ComponentType<
    StoryblokComponentProps<Extract<StoryblokComponentType, { component: key }>>
  >
}

export const STORYBLOK_COMPONENTS_MAP = {
  Accordion: withStoryblokSuspenseErrorBoundary(
    dynamic(async () =>
      import('./StoryblokAccordion').then(
        (module) => module.StoryblokAccordion,
      ),
    ),
  ),
  AccordionStack: withStoryblokSuspenseErrorBoundary(
    dynamic(async () =>
      import('./StoryblokAccordionStack').then(
        (module) => module.StoryblokAccordionStack,
      ),
    ),
  ),
  AdBanner: StoryblokAdBanner,
  AdviceCard: withStoryblokSuspenseErrorBoundary(
    dynamic(async () =>
      import('./StoryblokAdviceCard').then(
        (module) => module.StoryblokAdviceCard,
      ),
    ),
  ),
  AdvicePage: StoryblokAdvicePage,
  AkeneoCategoryCrossSell: withStoryblokSuspenseErrorBoundary(
    dynamic(async () =>
      import('./StoryblokAkeneoCategoryCrossSell').then(
        (module) => module.StoryblokAkeneoCategoryCrossSell,
      ),
    ),
  ),

  AkeneoCategoryPage: StoryblokAkeneoCategoryPage,
  BlogArticle: StoryblokBlogArticle,
  BrandIcon: withStoryblokSuspenseErrorBoundary(
    dynamic(async () => {
      return import('./StoryblokBrandIcon').then(
        (module) => module.StoryblokBrandIcon,
      )
    }),
  ),
  BrandIconListItem: withStoryblokSuspenseErrorBoundary(
    dynamic(async () =>
      import('./StoryblokList').then(
        (module) => module.StoryblokBrandIconListItem,
      ),
    ),
  ),

  BrandIllustration: withStoryblokSuspenseErrorBoundary(
    dynamic(async () =>
      import('./StoryblokBrandIllustration').then(
        (module) => module.StoryblokBrandIllustration,
      ),
    ),
  ),
  BrandIllustrationInfoCard: withStoryblokSuspenseErrorBoundary(
    dynamic(async () =>
      import('./StoryblokBrandIllustrationInfoCard').then(
        (module) => module.StoryblokBrandIllustrationInfoCard,
      ),
    ),
  ),

  BrandImageCard: withStoryblokSuspenseErrorBoundary(
    dynamic(async () => {
      return import('./StoryblokBrandImageCard/StoryblokBrandImageCard').then(
        (module) => module.StoryblokBrandImageCard,
      )
    }),
  ),
  BrandPage: StoryblokBrandPage,
  Button: StoryblokButton,
  ButtonV2: StoryblokButtonV2,
  Card: StoryblokCard,
  CardCarousel: StoryblokCardCarousel,
  CategoryAdBannersSmall: StoryblokCategoryAdBannersSmall,
  CategoryAdBannerWide: StoryblokCategoryAdBannerWide,
  CategoryInfoText: withStoryblokSuspenseErrorBoundary(
    dynamic(async () =>
      import('./StoryblokCategoryInfoText').then(
        (module) => module.StoryblokCategoryInfoText,
      ),
    ),
  ),
  CategorySection: StoryblokCategorySection,
  CategoryWideTeaserAd: StoryblokCategoryWideTeaserAd,
  ColumnCard: StoryblokColumnCard,
  ContainerLink: withStoryblokSuspenseErrorBoundary(
    dynamic(async () => {
      return import('./StoryblokContainerLink/StoryblokContainerLink').then(
        (module) => module.StoryblokContainerLink,
      )
    }),
  ),
  CounterIndicator: withStoryblokSuspenseErrorBoundary(
    dynamic(async () => {
      return import('./StoryblokCounterIndicator').then(
        (module) => module.StoryblokCounterIndicator,
      )
    }),
  ),
  CrossSellGrid: withStoryblokSuspenseErrorBoundary(
    dynamic(async () =>
      import('./StoryblokCrossSellGrid').then(
        (module) => module.StoryblokCrossSellGrid,
      ),
    ),
  ),
  CrossSellSlider: StoryblokCrossSellSlider,
  Flex: StoryblokFlex,
  FlexRow: StoryblokFlexRow,
  Grid: StoryblokGrid,
  GridCell: StoryblokGridCell,
  HeaderBanner: StoryblokHeaderBanner,
  HeadingV2: StoryblokHeadingV2,
  ImageCard: StoryblokImageCard,
  ImageTextHorizontalCard: StoryblokImageTextHorizontalCard,
  ImageV2: StoryblokImageV2,
  InfoCard: StoryblokInfoCard,
  List: withStoryblokSuspenseErrorBoundary(
    dynamic(async () =>
      import('./StoryblokList').then((module) => module.StoryblokList),
    ),
  ),
  ListButton: withStoryblokSuspenseErrorBoundary(
    dynamic(async () => {
      return import('./StoryblokListButton').then(
        (module) => module.StoryblokListButton,
      )
    }),
  ),
  MostSearchedKeywords: withStoryblokSuspenseErrorBoundary(
    dynamic(async () => {
      return import('./StoryblokMostSearchedKeywords/StoryblokMostSearchedKeywords').then(
        (module) => module.StoryblokMostSearchedKeywords,
      )
    }),
  ),
  Notification: StoryblokNotification,
  NowLpZipCodeElegibilityChecker: withStoryblokSuspenseErrorBoundary(
    dynamic(async () => {
      return import('./StoryblokNowLpZipCodeChecker').then(
        (module) => module.StoryblokNowLpZipCodeChecker,
      )
    }),
  ),

  NumberedListItem: withStoryblokSuspenseErrorBoundary(
    dynamic(async () =>
      import('./StoryblokList').then(
        (module) => module.StoryblokNumberedListItem,
      ),
    ),
  ),
  OptimizelyExperiment: withStoryblokSuspenseErrorBoundary(
    dynamic(async () => {
      return import('./StoryblokOptimizelyExperiment/StoryblokOptimizelyExperiment').then(
        (module) => module.StoryblokOptimizelyExperiment,
      )
    }),
  ),
  Page: StoryblokPage,
  Pill: dynamic(async () =>
    import('./StoryblokPill/StoryblokPill').then(
      (module) => module.StoryblokPill,
    ),
  ),
  PlainTextFaq: StoryblokPlainTextFaq,
  ProductBatteryV2: withStoryblokSuspenseErrorBoundary(
    dynamic(async () =>
      import('./StoryblokProductBatteryV2').then(
        (module) => module.StoryblokProductBatteryV2,
      ),
    ),
  ),
  ProductCard: StoryblokProductCard,
  ProductRatings: withStoryblokSuspenseErrorBoundary(
    dynamic(async () =>
      import('./StoryblokProductRatings').then(
        (module) => module.StoryblokProductRatings,
      ),
    ),
  ),
  // ProductSearch uses algolia which does not support suspense that's why we don't wrap it using withStoryblokSuspenseErrorBoundary
  // However, we still want to use dynamic import for code splitting
  ProductSearch: dynamic(async () =>
    import('./StoryblokProductSearch').then(
      (module) => module.StoryblokProductSearch,
    ),
  ),
  RedPointsLogin: withStoryblokSuspenseErrorBoundary(
    dynamic(async () =>
      import('./StoryblokRedPointsLogin').then(
        (module) => module.StoryblokRedPointsLogin,
      ),
    ),
  ),
  RichText: StoryblokRichText,
  Section: StoryblokSection,
  SectionHeading: withStoryblokSuspenseErrorBoundary(
    dynamic(async () =>
      import('./StoryblokSectionHeading').then(
        (module) => module.StoryblokSectionHeading,
      ),
    ),
  ),
  SectionV2: StoryblokSectionV2,
  ServicesList: StoryblokServicesList,
  ServicesListItem: withStoryblokSuspenseErrorBoundary(
    dynamic(async () =>
      import('./StoryblokServicesList/StoryblokServicesListItem').then(
        (module) => module.StoryblokServicesListItem,
      ),
    ),
  ),
  Slider: withStoryblokSuspenseErrorBoundary(
    dynamic(async () =>
      import('./StoryblokSlider').then((module) => module.StoryblokSlider),
    ),
  ),
  Spacing: StoryblokSpacing,
  StepStar: StoryblokStepStar,
  SystemIconListItem: withStoryblokSuspenseErrorBoundary(
    dynamic(async () =>
      import('./StoryblokList').then(
        (module) => module.StoryblokSystemIconListItem,
      ),
    ),
  ),
  Tab: withStoryblokSuspenseErrorBoundary(
    dynamic(async () =>
      import('./StoryblokTabs').then((module) => module.StoryblokTab),
    ),
  ),
  Tabs: withStoryblokSuspenseErrorBoundary(
    dynamic(async () =>
      import('./StoryblokTabs').then((module) => module.StoryblokTabs),
    ),
  ),
  TextCard: withStoryblokSuspenseErrorBoundary(
    dynamic(async () => {
      return import('./StoryblokTextCard/StoryblokTextCard').then(
        (module) => module.StoryblokTextCard,
      )
    }),
  ),
  Video: withStoryblokSuspenseErrorBoundary(
    dynamic(async () => {
      return import('./StoryblokVideo/StoryblokVideo').then(
        (module) => module.StoryblokVideo,
      )
    }),
  ),
  VoucherCard: StoryblokVoucherCard,
  VoucherCardV2: StoryblokVoucherCardV2,
} satisfies Partial<StoryblokComponentMap>
